import React from 'react'
import { Box, Button, Checkbox, Divider, FormControlLabel, InputLabel, Stack, Typography } from '@mui/material'
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'

import type { CountryWithMCC } from '@/src/types/country'
import ItineraryAutoComplete from '@/src/components/common/itinerary/auto-complete'
import ItineraryTextField from '@/src/components/common/itinerary/itinearyTextField'
import { HOLIDAYS_RADIUS } from '@/src/utils/holidays'

import PoweredByWrapper from '../poweredBy'

interface FieldLabels {
    fullName: string
    namePlaceholder: string
    email: string
    emailPlaceholder: string
    mobile: string
    mobilePlaceholder: string
    promo: string
    promoPlaceholder: string
    select: string
}

interface ConsentState {
    deals: boolean
    contact: boolean
    terms: boolean
}

type FieldKey = 'name' | 'email' | 'mobile' | 'promo'

interface EnquiryCardProps {
    /** Namespaces the field ids so a second copy of the form can coexist on one page. */
    idPrefix: string
    /** Anchor the mobile docked bar scrolls to. */
    anchorId?: string
    title: string
    subtitle?: string
    priceLabel?: string
    /** Pre-formatted, e.g. 'AED 12,599'. */
    price?: string
    labels: FieldLabels
    values: Record<FieldKey, string>
    errors: Partial<Record<FieldKey | 'countryCode' | 'terms', string>>
    onFieldChange: (field: FieldKey, value: string) => void
    countryOptions: CountryWithMCC[]
    countryLoading: boolean
    selectedCountry: CountryWithMCC | null
    onCountryChange: (country: CountryWithMCC | null) => void
    countryOptionLabel: (country: CountryWithMCC) => string
    countryValueLabel: (country: CountryWithMCC) => string
    countryGroupBy: (country: CountryWithMCC) => string
    consents: ConsentState
    consentLabels: Record<keyof ConsentState, string>
    onConsentChange: (key: keyof ConsentState, checked: boolean) => void
    submitLabel: string
    onSubmit: () => void
    isSubmitting: boolean
    /** Set once the enquiry is sent; replaces the submit button. */
    sentMessage?: string
    privacyNote: string
    expertLabel: string
    phoneLabel?: string
    emailAddress?: string
    emailHref?: string
    onContactClick: () => void
    isMobile: boolean
}

const Field: React.FC<{ label: string; htmlFor: string; children: React.ReactNode }> = ({
    label,
    htmlFor,
    children
}) => (
    <Box>
        <InputLabel
            shrink
            htmlFor={htmlFor}
            sx={{ fontSize: '0.875rem', fontWeight: 700, color: 'textColors.primary', mb: 0.5 }}
        >
            {label}
        </InputLabel>
        {children}
    </Box>
)

const EnquiryCard: React.FC<EnquiryCardProps> = ({
    anchorId,
    title,
    subtitle,
    priceLabel,
    price,
    labels,
    values,
    errors,
    onFieldChange,
    countryOptions,
    countryLoading,
    selectedCountry,
    onCountryChange,
    countryOptionLabel,
    countryValueLabel,
    countryGroupBy,
    consents,
    consentLabels,
    onConsentChange,
    submitLabel,
    onSubmit,
    isSubmitting,
    sentMessage,
    privacyNote,
    idPrefix,
    expertLabel,
    phoneLabel,
    emailAddress,
    emailHref,
    onContactClick,
    isMobile
}) => {
    return (
        <Box
            component="form"
            id={anchorId}
            noValidate
            onSubmit={event => {
                event.preventDefault()
                onSubmit()
            }}
            sx={{
                scrollMarginTop: isMobile ? '132px' : '124px',
                borderRadius: HOLIDAYS_RADIUS.SURFACE,
                bgcolor: 'background.paper',
                border: '2px solid',
                borderColor: 'primary.main',
                boxShadow: 3,
                overflow: 'hidden'
            }}
        >
            {/*
             * The form used to wear the same 1px neutral border as every section card, so the one
             * thing on the page asking for the reader's details looked like more content. The brand
             * header and border make it the page's single obvious call to action.
             */}
            <Box
                sx={{
                    px: isMobile ? 2 : 2.5,
                    py: isMobile ? 1.75 : 2,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText'
                }}
            >
                <Typography component="h2" sx={{ fontSize: '1.375rem', fontWeight: 700 }}>
                    {title}
                </Typography>
                {subtitle && (
                    <Typography sx={{ fontSize: '0.813rem', mt: 0.25, opacity: 0.85 }}>{subtitle}</Typography>
                )}
            </Box>

            <Stack spacing={2} sx={{ p: isMobile ? 2 : 2.5 }}>

                {price && (
                    <Box sx={{ p: 1.5, borderRadius: HOLIDAYS_RADIUS.NESTED, bgcolor: 'primary.100' }}>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: 'textColors.primary' }}>
                            {priceLabel} {price}
                        </Typography>
                    </Box>
                )}

                <Field label={labels.fullName} htmlFor={`${idPrefix}-name`}>
                    <ItineraryTextField
                        id={`${idPrefix}-name`}
                        placeholder={labels.namePlaceholder}
                        value={values.name}
                        onChange={event => onFieldChange('name', event.target.value)}
                        error={Boolean(errors.name)}
                        helperText={errors.name}
                        autoComplete="name"
                    />
                </Field>

                <Field label={labels.email} htmlFor={`${idPrefix}-email`}>
                    <ItineraryTextField
                        id={`${idPrefix}-email`}
                        type="email"
                        placeholder={labels.emailPlaceholder}
                        value={values.email}
                        onChange={event => onFieldChange('email', event.target.value)}
                        error={Boolean(errors.email)}
                        helperText={errors.email}
                        autoComplete="email"
                    />
                </Field>

                {/* Dial code and number — the same pairing the itinerary contact form uses. */}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <ItineraryAutoComplete
                            disableClearable
                            loading={countryLoading}
                            label={labels.mobile}
                            options={countryOptions}
                            value={selectedCountry}
                            onChange={onCountryChange}
                            getOptionLabel={countryOptionLabel}
                            renderValueForMobileScreen={value => countryValueLabel(value)}
                            renderOption={(props, option) => (
                                <Typography {...props} key={(props as { key?: string }).key} sx={{ fontSize: 13 }}>
                                    {countryOptionLabel(option)}
                                </Typography>
                            )}
                            groupBy={countryGroupBy}
                            placeholder={labels.select}
                            error={Boolean(errors.countryCode)}
                            errorMessage={errors.countryCode}
                        />
                    </Box>

                    <Box sx={{ flex: 2, minWidth: 0 }}>
                        {/* Hidden label keeps this field's baseline aligned with the selector's. */}
                        <InputLabel shrink htmlFor={`${idPrefix}-mobile`} sx={{ opacity: 0, mb: 0.5 }}>
                            {labels.mobile}
                        </InputLabel>
                        <ItineraryTextField
                            id={`${idPrefix}-mobile`}
                            placeholder={labels.mobilePlaceholder}
                            value={values.mobile}
                            onChange={event => onFieldChange('mobile', event.target.value)}
                            error={Boolean(errors.mobile)}
                            helperText={errors.mobile}
                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                            autoComplete="tel-national"
                        />
                    </Box>
                </Box>

                <Field label={labels.promo} htmlFor={`${idPrefix}-promo`}>
                    <ItineraryTextField
                        id={`${idPrefix}-promo`}
                        placeholder={labels.promoPlaceholder}
                        value={values.promo}
                        onChange={event => onFieldChange('promo', event.target.value)}
                    />
                </Field>

                <Stack spacing={0.25}>
                    {(['deals', 'contact', 'terms'] as const).map(key => (
                        <FormControlLabel
                            key={key}
                            control={
                                <Checkbox
                                    checked={consents[key]}
                                    onChange={event => onConsentChange(key, event.target.checked)}
                                    size="small"
                                />
                            }
                            label={
                                <Typography sx={{ fontSize: '12px', color: 'textColors.secondary' }}>
                                    {consentLabels[key]}
                                </Typography>
                            }
                            sx={{ alignItems: 'flex-start', m: 0, '& .MuiCheckbox-root': { pt: 0.25 } }}
                        />
                    ))}
                    {errors.terms && (
                        <Typography sx={{ fontSize: '0.75rem', color: 'error.main', pl: 1.5 }}>
                            {errors.terms}
                        </Typography>
                    )}
                </Stack>

                {sentMessage ? (
                    <Typography
                        role="status"
                        sx={{ py: 1.25, fontSize: '0.875rem', fontWeight: 700, textAlign: 'center', color: 'success.main' }}
                    >
                        {sentMessage}
                    </Typography>
                ) : (
                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        loading={isSubmitting}
                        sx={{ py: 1.25, fontWeight: 700 }}
                    >
                        {submitLabel}
                    </Button>
                )}

                <PoweredByWrapper height={18} align="center" />

                <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', justifyContent: 'center' }}>
                    <LockOutlinedIcon sx={{ fontSize: 14, color: 'textColors.subheading' }} />
                    <Typography sx={{ fontSize: '0.688rem', color: 'textColors.subheading' }}>{privacyNote}</Typography>
                </Stack>

                {(phoneLabel || emailHref) && (
                    <>
                        <Divider />
                        <Stack spacing={1}>
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'textColors.subtitle' }}>
                                {expertLabel}
                            </Typography>
                            {phoneLabel && (
                                <Box
                                    component="span"
                                    dir="ltr"
                                    sx={{ color: 'primary.main', fontSize: '0.875rem', fontWeight: 600 }}
                                >
                                    {phoneLabel}
                                </Box>
                            )}
                            {emailHref && (
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    component="a"
                                    href={emailHref}
                                    onClick={onContactClick}
                                    sx={{
                                        alignItems: 'center',
                                        textDecoration: 'none',
                                        color: 'primary.main',
                                        fontSize: '0.875rem',
                                        fontWeight: 600
                                    }}
                                >
                                    <MailOutlineRoundedIcon sx={{ fontSize: 16 }} />
                                    <Box component="span" dir="ltr">
                                        {emailAddress}
                                    </Box>
                                </Stack>
                            )}
                        </Stack>
                    </>
                )}
            </Stack>
        </Box>
    )
}

export { EnquiryCard }
export type { EnquiryCardProps }
export default EnquiryCard
